import {
  ClearFiltersHeader,
  getColumns,
  TableBody,
  TableColumnInfoBox,
  TableHeader,
  TableHeaderItem,
  TableRow,
  TableSearchEdit,
  TableVisibilitySensor
} from "collections/Table";
import {
  CategoryTitle,
  NoResultsIndicator,
  PolyIcon,
  PolyPlusBadge
} from "elements";
import { Link } from "polyrouter";
import { ReactNode, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { State, useTypesafeActions } from "store";
import { TABLES, TABLE_COLUMN_STATE } from "store/userMeta";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import { generateTableHeaders } from "utilities/tableUtils";
import { Layout } from "views/Main/Layouts";
import { SortState } from "store/filters";
import { ActionType, createStandardAction, getType } from "typesafe-actions";
import { format } from "date-fns";
import {
  AuditLogFragment,
  Direction,
  EventType,
  LogFilter,
  LoginType,
  useAuditLogsQuery
} from "generated/graphql";
import { hasValue } from "utilities/filters/filters";
import { usePolyPlusEnabled } from "utilities/polyPlus";
import { PolyPlusInfoBox } from "elements/PolyPlus/PolyPlus";

export const AUDIT_LOG_TIME_FORMAT = "dd LLL, yyyy HH:mm:ss OOOO";

type AuditLogFilter = Record<string, string>;
interface AuditLogTableState {
  filters: AuditLogFilter;
  searchTerm?: string;
  cursor: number;
  sort: SortState;
}

const Actions = {
  updateFilters: createStandardAction("UpdateFilters")<AuditLogFilter>(),
  updateSearchTerm: createStandardAction("UpdateSearchTerm")<
    string | undefined
  >(),
  updateCursor: createStandardAction("UpdateCursor")<number>(),
  updateSort: createStandardAction("UpdateSort")<SortState>()
};

type ActionTypes = typeof Actions;

function reducer(
  state: AuditLogTableState,
  action: ActionType<ActionTypes>
): AuditLogTableState {
  switch (action.type) {
    case getType(Actions.updateFilters):
      return {
        ...state,
        cursor: 0,
        filters: action.payload
      };
    case getType(Actions.updateSearchTerm):
      const term = action.payload;
      return {
        ...state,
        cursor: term !== undefined ? 0 : state.cursor,
        searchTerm: term
      };
    case getType(Actions.updateCursor):
      return {
        ...state,
        cursor: action.payload
      };
    case getType(Actions.updateSort):
      return {
        ...state,
        cursor: 0,
        sort: action.payload
      };
  }
}

const { COLUMNS } = TABLES.AUDIT_LOGS;
const columnMappingData = {
  [COLUMNS.EVENT_TYPE]: {
    prefix: "EVENT_TYPE",
    enm: EventType
  },
  [COLUMNS.LOGIN_TYPE]: {
    prefix: "LOGIN_TYPE",
    enm: LoginType
  }
};

const mapFieldToGraphQL = {
  [COLUMNS.LOGIN_TYPE]: "apollo_client_name"
};

const LENS_WEB_UI_CLIENT_NAME = "lens-web-ui";

export const AuditLogs: React.FC<{
  tenantId: string;
}> = ({ tenantId }) => {
  const tableHeaderRef = useRef<HTMLTableCellElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const { polyPlusEnabled, fetching: polyPlusFetching } = usePolyPlusEnabled(
    tenantId
  );

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.AUDIT_LOGS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  const defaultFilters = {
    [COLUMNS.EVENT_TYPE]: "",
    [COLUMNS.LOGIN_TYPE]: ""
  };

  const initialState: AuditLogTableState = {
    filters: defaultFilters,
    searchTerm: undefined,
    cursor: 0,
    sort: {
      field: COLUMNS.USER,
      direction: "ASC"
    }
  };

  const [
    { filters, searchTerm, cursor, sort },
    { updateFilters, updateSearchTerm, updateCursor, updateSort }
  ] = useTypesafeActions<AuditLogTableState, ActionTypes>(
    reducer,
    initialState,
    Actions
  );

  const columnTranslations = {
    [COLUMNS.USER]: t("USER"),
    [COLUMNS.TIME]: t("TIME"),
    [COLUMNS.EVENT_TYPE]: t("EVENT_TYPE"),
    [COLUMNS.LOGIN_TYPE]: t("LOGIN_TYPE"),
    [COLUMNS.IP_ADDRESS]: t("PUBLIC_IP_ADDRESS"),
    [COLUMNS.AUTHORIZED]: t("AUTHORIZED"),
    [COLUMNS.EVENT_DETAILS]: t("EVENT_DETAILS")
  };
  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const createFilterOptions = (column: string) => {
    const { prefix, enm } = columnMappingData[column];
    let options = Object.values(enm);
    if (enm === LoginType) {
      // At the moment we do not support filtering by LoginType.Unknown
      options = options.filter(value => value !== LoginType.Unknown);
    }
    return options.map(value => ({
      value,
      text: t(`${prefix}_${value}` as LocalKeys)
    }));
  };

  const filterOptions = {
    [COLUMNS.EVENT_TYPE]: createFilterOptions(COLUMNS.EVENT_TYPE),
    [COLUMNS.LOGIN_TYPE]: createFilterOptions(COLUMNS.LOGIN_TYPE)
  };

  const getTableHeaders = (column: string, title: string) => {
    const filterValue = filters[column];
    const filterable = filterValue !== undefined;
    const filter = filterable
      ? {
          filterValue,
          filterOptions: filterOptions[column],
          onFilter: (field: string, value: string) => {
            updateFilters({
              ...filters,
              ...{ [field]: value }
            });
          }
        }
      : undefined;

    // Do not allow sort on Event Details column since it doesn't make much sense for it to be sortable
    const sortable = column !== COLUMNS.EVENT_DETAILS;

    return (
      <TableHeaderItem
        fieldName={column}
        title={title}
        rightTitle={
          column === COLUMNS.EVENT_DETAILS ? (
            <PolyPlusBadge className="ml-5 d-flex small" />
          ) : (
            undefined
          )
        }
        key={column}
        sort={
          sortable
            ? {
                currentSort: sort,
                onSort: updateSort
              }
            : undefined
        }
        filter={filter}
        dataTestId={`header-${column}`}
        ref={column === COLUMNS.EVENT_DETAILS ? tableHeaderRef : undefined}
      />
    );
  };

  const { data, fetching, stale } = useAuditLogsQuery({
    variables: {
      filter: {
        AND: Object.entries(filters)
          .filter(([key]) => hasValue(filters, key))
          .map(([key, value]) => {
            let filter: LogFilter = {};
            if (key === COLUMNS.LOGIN_TYPE) {
              const operator: keyof LogFilter | null =
                value === LoginType.Webui
                  ? "eq"
                  : value === LoginType.Api
                  ? "ne"
                  : null;
              if (operator) {
                filter = {
                  field: mapFieldToGraphQL[key],
                  [operator]: LENS_WEB_UI_CLIENT_NAME
                };
              }
            } else {
              filter = {
                field: key,
                eq: value
              };
            }

            return filter;
          }),
        searchTerm
      },
      from: cursor,
      limit: 100,
      sort: {
        fields: [
          {
            name: mapFieldToGraphQL[sort.field] ?? sort.field,
            direction: sort.direction as Direction
          }
        ]
      },
      tenantId
    }
  });

  const auditLogs = data?.getAuditLogs ?? [];
  const endCursor = auditLogs.length;
  const hasNextPage = endCursor !== cursor;
  const loading = fetching || stale || polyPlusFetching;
  const tableLoading = loading && !cursor;

  const onChange = (isVisible: boolean) => {
    if (isVisible) {
      updateCursor(endCursor);
    }
  };

  const hasActiveFilter = Object.keys(filters).some(filter =>
    hasValue(filters, filter)
  );

  const { Content } = Layout;

  const getTableElements = useCallback(
    () => ({
      tableHeader: tableHeaderRef.current,
      tableContainer: tableContainerRef.current
    }),
    []
  );

  return (
    <Content>
      <CategoryTitle className="card title-card" title={t("AUDIT_LOGS")} />
      <div className="table-actions mb-15">
        <TableSearchEdit
          search={{
            onSearch: updateSearchTerm,
            searchPlaceholder: `${t("SEARCH")} ${
              columnTranslations[COLUMNS.USER]
            }, ${columnTranslations[COLUMNS.IP_ADDRESS]}`
          }}
          edit={{
            tableName: TABLE_NAME,
            shownColumns: shown,
            hiddenColumns: hidden
          }}
        />
      </div>
      <div className="table-container" ref={tableContainerRef}>
        <table
          className="table custom-table table-filterable"
          data-testid="audit-log-table"
        >
          <TableHeader>
            {generateTableHeaders(shown, getTableHeaders)}
            <ClearFiltersHeader
              showClearFilters={hasActiveFilter}
              onClearFilters={() => updateFilters(defaultFilters)}
            />
          </TableHeader>
          <TableBody
            loading={tableLoading}
            cols={shown.length}
            accommodateScrollbar
          >
            {auditLogs.map(auditLog => (
              <AuditLogRow
                tenantId={tenantId}
                auditLog={auditLog}
                shownColumns={shown.map(column => column.key)}
                polyPlusEnabled={polyPlusEnabled}
                key={auditLog.id}
              />
            ))}
            {hasNextPage && <TableVisibilitySensor onChange={onChange} />}
            {!loading && !auditLogs.length ? (
              <tr>
                <td>
                  <NoResultsIndicator
                    className="inline mt-5"
                    title={t("NO_AUDIT_LOGS")}
                  />
                </td>
              </tr>
            ) : null}
          </TableBody>
        </table>
        {!polyPlusEnabled && !tableLoading && auditLogs.length ? (
          <TableColumnInfoBox
            getTableElements={getTableElements}
            shownColumns={shownColumns}
          >
            <PolyPlusInfoBox tenantId={tenantId} />
          </TableColumnInfoBox>
        ) : null}
      </div>
    </Content>
  );
};

const AuditLogRow: React.FC<{
  tenantId: string;
  auditLog: AuditLogFragment;
  shownColumns: Array<string>;
  polyPlusEnabled: boolean | undefined;
}> = ({ tenantId, auditLog, shownColumns, polyPlusEnabled }) => {
  const { t } = useTranslation();
  const { COLUMNS } = TABLES.AUDIT_LOGS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.USER:
        const { userId, username } = auditLog;
        content = (
          <Link
            to={`/admin/accounts/details/${tenantId}/members/details/${userId}`}
          >
            {handleNullFields(username)}
          </Link>
        );
        break;
      case COLUMNS.TIME:
        content = format(new Date(auditLog.logTime), AUDIT_LOG_TIME_FORMAT);
        break;
      case COLUMNS.EVENT_TYPE:
      case COLUMNS.LOGIN_TYPE:
        const value = auditLog[column as keyof AuditLogFragment];
        const { prefix } = columnMappingData[column];
        content =
          value && value !== LoginType.Unknown
            ? t(`${prefix}_${value}` as LocalKeys)
            : "-";
        break;
      case COLUMNS.EVENT_DETAILS:
        if (polyPlusEnabled) {
          const { eventType, eventDetails } = auditLog;
          let details = {};

          switch (eventType) {
            case EventType.InviteUser:
            case EventType.DisassociateWithResource:
              details = {
                user: eventDetails
              };
              break;
            case EventType.SwitchRole:
              const [user, role] = eventDetails?.split(",") ?? [];
              details = {
                user: user.trim(),
                role: t(role.trim() as LocalKeys)
              };
              break;
            default:
              break;
          }
          content = (
            <span>
              {eventDetails
                ? t(`EVENT_DETAILS_${eventType}` as LocalKeys, details)
                : "-"}
            </span>
          );
        } else {
          content = <span className="blur">Lorem ipsum dolor</span>;
        }
        break;
      case COLUMNS.AUTHORIZED:
        content = (
          <span className="authorized-info pl-1 text-center d-inline-block">
            {auditLog.authorized ? (
              <PolyIcon
                icon="check_circle_fill"
                data-testid="authorized-icon"
              />
            ) : (
              "-"
            )}
          </span>
        );

        break;
      default:
        const columnValue = auditLog[column as keyof AuditLogFragment];
        content = columnValue ? columnValue.toString() : "";
        break;
    }

    return (
      <td key={column} data-testid={column}>
        {content}
      </td>
    );
  };

  return (
    <TableRow checked={false} data-testid={`audit-log-row-${auditLog.id}`}>
      {shownColumns.map(getTableContent)}
    </TableRow>
  );
};
